import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import { KaajActions, KaajBadge, KaajHeading, KaajSectionShell } from "../../shared/kaaj-content";
import { RichTextHtml } from "../../RichTextHtml";
import type { IPageCardsBlockProps, PageCardsBlockItem } from "./PageCardsBlock.props";

const Image = resolveKaajPrimitive("Image");
const Link = resolveKaajPrimitive("Link");

const getSafeRel = (href: string, target?: string) =>
  target === "_blank" || href.startsWith("http") ? "noopener noreferrer" : undefined;

const gridClassName = (count: number) => {
  if (count === 2) return "grid-cols-1 md:grid-cols-2";
  if (count % 4 === 0) return "grid-cols-1 md:grid-cols-2 xl:grid-cols-4";
  return "grid-cols-1 md:grid-cols-2 xl:grid-cols-3";
};

const getImageStyle = (focalPoint?: { x: number; y: number } | null) => {
  if (!focalPoint) return undefined;

  return { objectPosition: `${focalPoint.x}% ${focalPoint.y}%` };
};

const PageCardMedia = ({ item }: { item: PageCardsBlockItem }) => {
  const src = item.image?.defaultSrc;
  const alt = item.image?.alternateText;

  if (!src) {
    return null;
  }

  return (
    <figure className="relative aspect-[4/3] overflow-hidden border-b border-[#D9E1FF] bg-[linear-gradient(135deg,#EEF2FF_0%,#FFFFFF_54%,#FFF4FB_100%)] p-3">
      <div className="h-full w-full overflow-hidden rounded-[1.25rem] border border-white/80 bg-white shadow-[inset_0_0_0_1px_rgba(217,225,255,0.58)]">
        <Image
          alt={alt ?? ""}
          className="h-full w-full object-cover"
          height={item.image?.height ?? undefined}
          loading="lazy"
          sizes={item.image?.sizes ?? "(min-width: 1280px) 24rem, (min-width: 768px) 45vw, 92vw"}
          src={src}
          srcSet={item.image?.srcSet ?? undefined}
          style={getImageStyle(item.image?.focalPoint)}
          width={item.image?.width ?? undefined}
        />
      </div>
    </figure>
  );
};

const PageCard = ({ item }: { item: PageCardsBlockItem }) => {
  return (
    <article className="h-full">
      <Link
        aria-label={item.link.ariaLabel}
        className="flex h-full min-w-0 flex-col overflow-hidden rounded-[1.9rem] border border-[#D9E1FF] bg-white shadow-[0_22px_64px_rgba(33,68,255,0.10)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2144FF]"
        href={item.link.href}
        rel={getSafeRel(item.link.href, item.link.target ?? undefined)}
        target={item.link.target ?? undefined}
      >
        <PageCardMedia item={item} />

        <div className="flex flex-1 flex-col p-6 sm:p-7">
          <div className="mb-5 flex flex-wrap items-center gap-2">
            {item.category && <KaajBadge>{item.category}</KaajBadge>}
            {item.tags?.map((tag) => (
              <span
                key={tag.tag}
                className="inline-flex rounded-full border border-[#D9E1FF] bg-white px-3 py-1 text-xs font-medium text-[#4B5568]"
              >
                {tag.tag}
              </span>
            ))}
          </div>

          <h3 className="text-balance text-[1.35rem] font-semibold leading-tight tracking-[-0.03em] text-[#151927] sm:text-2xl">
            {item.heading}
          </h3>
          <RichTextHtml
            className="mt-4 flex-1 text-pretty text-sm leading-7 text-[#4B5568] sm:text-base"
            html={item.subheading}
          />

          <span className="mt-6 inline-flex min-h-11 items-center text-sm font-bold text-[#2144FF]">
            {item.link.text}
            <span aria-hidden="true" className="ml-2">
              →
            </span>
          </span>
        </div>
      </Link>
    </article>
  );
};

export const PageCardsBlock = ({
  parentClass,
  eyebrow,
  heading,
  subheading,
  anchorId,
  items,
  buttons,
  spacing,
}: IPageCardsBlockProps) => {
  const actions = buttons ?? [];

  if (!items?.length && !eyebrow && !heading && !subheading && actions.length === 0) {
    return null;
  }

  return (
    <KaajSectionShell id={anchorId ?? undefined} className={parentClass} spacing={spacing}>
      {(eyebrow || heading || subheading || actions.length > 0) && (
        <div className="mb-10 flex flex-col gap-5 lg:mb-12 lg:flex-row lg:items-end lg:justify-between [&>header]:mb-0">
          <KaajHeading
            align="left"
            eyebrow={eyebrow ?? undefined}
            heading={heading ?? undefined}
            subheading={subheading ?? undefined}
          />
          <KaajActions actions={actions} className="shrink-0 lg:justify-end" />
        </div>
      )}

      {items?.length ? (
        <div className={cn("grid gap-5 lg:items-stretch", gridClassName(items.length))}>
          {items.map((item) => (
            <div key={`${item.heading}-${item.link.href}`}>
              <PageCard item={item} />
            </div>
          ))}
        </div>
      ) : null}
    </KaajSectionShell>
  );
};

PageCardsBlock.displayName = "Block::PageCardsBlock";
